import type { FlatCoordinates } from "$lib/features/_other/coordinates.type";

export interface RoutePointSample {
    coordinates: FlatCoordinates;
    bearing: number; // 0..360 degrees
    traversedDistance: number; // meters
    totalDistance: number; // meters
    progress: number; // 0..1
    segmentIndex: number;
}

/**
 * Calculates the Haversine distance between two coordinates in meters.
 */
export function getHaversineDistanceMeters(coord1: FlatCoordinates, coord2: FlatCoordinates): number {
    const R = 6371000; // Earth radius in meters
    const lat1 = (coord1[1] * Math.PI) / 180;
    const lat2 = (coord2[1] * Math.PI) / 180;
    const deltaLat = ((coord2[1] - coord1[1]) * Math.PI) / 180;
    const deltaLng = ((coord2[0] - coord1[0]) * Math.PI) / 180;

    const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Computes forward bearing from coord1 to coord2 in degrees (0..360).
 */
export function getBearing(coord1: FlatCoordinates, coord2: FlatCoordinates): number {
    const lon1 = (coord1[0] * Math.PI) / 180;
    const lat1 = (coord1[1] * Math.PI) / 180;
    const lon2 = (coord2[0] * Math.PI) / 180;
    const lat2 = (coord2[1] * Math.PI) / 180;

    const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
    const bearing = (Math.atan2(y, x) * 180) / Math.PI;

    return (bearing + 360) % 360;
}

/**
 * Linearly interpolates an angle around the 360-degree circle via the shortest route.
 */
export function lerpAngle(from: number, to: number, t: number): number {
    let diff = ((to - from + 180) % 360) - 180;
    if (diff < -180) diff += 360;
    return (from + diff * t + 360) % 360;
}

/**
 * Safely extracts [lng, lat] tuples from any coordinate structure (objects, arrays, or GeoJSON)
 */
export function extractRouteCoordinates(route: any, stops?: any[]): FlatCoordinates[] {
    function toLngLat(item: any): FlatCoordinates | null {
        if (!item) return null;
        // [lng, lat]
        if (Array.isArray(item) && Number.isFinite(item[0]) && Number.isFinite(item[1])) {
            return [item[0], item[1]];
        }
        // { longitude, latitude } (Velatlas format)
        if (Number.isFinite(item.longitude) && Number.isFinite(item.latitude)) {
            return [item.longitude, item.latitude];
        }
        // { lng, lat } or { lon, lat }
        if (Number.isFinite(item.lng) && Number.isFinite(item.lat)) return [item.lng, item.lat];
        if (Number.isFinite(item.lon) && Number.isFinite(item.lat)) return [item.lon, item.lat];
        return null;
    }

    function flatten(data: any): FlatCoordinates[] {
        if (!data) return [];
        const single = toLngLat(data);
        if (single) return [single];

        if (Array.isArray(data)) {
            return data.flatMap(flatten);
        }
        if (typeof data === 'object') {
            if (data.coordinates) return flatten(data.coordinates);
            if (data.multiLineGeometry) return flatten(data.multiLineGeometry);
        }
        return [];
    }

    const pts = flatten(route?.multiLineGeometry ?? route);

    // Fallback to tour stops if route geometry is empty
    if (pts.length < 2 && stops && stops.length >= 2) {
        return stops
            .map((s) => toLngLat(s.place?.coordinates ?? s.coordinates))
            .filter((c): c is FlatCoordinates => c !== null);
    }

    return pts;
}

export function calculateDistance(from: FlatCoordinates, to: FlatCoordinates): number {
    return Math.round(getHaversineDistanceMeters(from, to));
}

/**
 * Precomputes cumulative distances along a polyline for efficient O(log N) sampling.
 */
export class PolylineSampler {
    private readonly points: FlatCoordinates[];
    private readonly cumDistances: number[] = [0];
    public readonly totalDistance: number;

    constructor(points: FlatCoordinates[]) {
        this.points = points;
        for (let i = 0; i < points.length - 1; i++) {
            const dist = getHaversineDistanceMeters(points[i], points[i + 1]);
            this.cumDistances.push(this.cumDistances[i] + dist);
        }
        this.totalDistance = this.cumDistances[this.cumDistances.length - 1] || 0;
    }

    public sampleAtDistance(targetDist: number): RoutePointSample {
        const clampedDist = Math.max(0, Math.min(targetDist, this.totalDistance));
        if (this.points.length === 0) {
            return {
                coordinates: [0, 0],
                bearing: 0,
                traversedDistance: 0,
                totalDistance: 0,
                progress: 0,
                segmentIndex: 0
            };
        }

        if (this.points.length === 1 || this.totalDistance === 0) {
            return {
                coordinates: this.points[0],
                bearing: 0,
                traversedDistance: 0,
                totalDistance: 0,
                progress: 1,
                segmentIndex: 0
            };
        }

        // Binary search to find segment
        let low = 0;
        let high = this.cumDistances.length - 1;
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            if (this.cumDistances[mid] <= clampedDist) {
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        const index = Math.max(0, Math.min(high, this.points.length - 2));
        const segStartDist = this.cumDistances[index];
        const segEndDist = this.cumDistances[index + 1];
        const segLength = segEndDist - segStartDist;

        const factor = segLength > 0 ? (clampedDist - segStartDist) / segLength : 0;
        const p1 = this.points[index];
        const p2 = this.points[index + 1];

        const lng = p1[0] + (p2[0] - p1[0]) * factor;
        const lat = p1[1] + (p2[1] - p1[1]) * factor;
        const bearing = getBearing(p1, p2);

        return {
            coordinates: [lng, lat],
            bearing,
            traversedDistance: clampedDist,
            totalDistance: this.totalDistance,
            progress: this.totalDistance > 0 ? clampedDist / this.totalDistance : 0,
            segmentIndex: index
        };
    }
}